import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, Puzzle, Eye, Users, Briefcase, Linkedin, Mail, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const tabs = [
  { id: 'histoire', icon: Clock, labelFr: 'Notre Histoire', labelEn: 'Our History' },
  { id: 'concept', icon: Puzzle, labelFr: 'Notre Concept', labelEn: 'Our Concept' },
  { id: 'vision', icon: Eye, labelFr: 'Vision & Philosophie', labelEn: 'Vision & Philosophy' },
  { id: 'equipe', icon: Users, labelFr: 'Équipe Dirigeante', labelEn: 'Leadership Team' },
  { id: 'emplois', icon: Briefcase, labelFr: 'Rejoignez-nous', labelEn: 'Join Us' },
];

const timeline = [
  { year: '2019', titleFr: '2019', titleEn: '2019', descFr: 'Définition du concept de complémentarité entre les programmes français et américains et de la double scolarité pour un programme unique.', descEn: 'Definition of the complementarity concept between French and American programs and dual schooling for a unique program.' },
  { year: '2020', titleFr: 'Sept. 2020', titleEn: 'Sept. 2020', descFr: '1ère rentrée d\'OFA Lycée USA avec plus de 80 élèves de la 6ème à la Première.', descEn: 'First opening of OFA Lycée USA with over 80 students from 6th to 11th grade.' },
  { year: '2021', titleFr: 'Sept. 2021', titleEn: 'Sept. 2021', descFr: 'Plus de 200 élèves du CE1 à la Terminale.', descEn: 'Over 200 students from 2nd grade to 12th grade.' },
  { year: '2022', titleFr: 'Mai 2022', titleEn: 'May 2022', descFr: 'Obtention de l\'accréditation COGNIA qui permet la délivrance du High School Diploma. Réseau de 40 000 établissements dans 100+ pays.', descEn: 'COGNIA accreditation obtained, enabling the delivery of the High School Diploma. Network of 40,000 institutions in 100+ countries.' },
  { year: '2023', titleFr: 'Sept. 2023', titleEn: 'Sept. 2023', descFr: 'Ouverture d\'OFA Lycée UK. Plus de 300 élèves à travers le monde de la Grande Section à la Terminale.', descEn: 'Opening of OFA Lycée UK. Over 300 students worldwide from Kindergarten to 12th grade.' },
  { year: '2024', titleFr: 'Sept. 2024', titleEn: 'Sept. 2024', descFr: 'Ouverture d\'OFA Lycée France. Plus de 400 élèves à travers le monde.', descEn: 'Opening of OFA Lycée France. Over 400 students worldwide.' },
  { year: '2025', titleFr: 'Sept. 2025', titleEn: 'Sept. 2025', descFr: 'Plus de 800 élèves à travers le monde. 100% de réussite au baccalauréat et 100% de mentions.', descEn: 'Over 800 students worldwide. 100% baccalaureate pass rate with 100% honors.' },
];

const stats = [
  { valueFr: '800+', valueEn: '800+', labelFr: 'Élèves', labelEn: 'Students' },
  { valueFr: '9', valueEn: '9', labelFr: 'Pays', labelEn: 'Countries' },
  { valueFr: '2020', valueEn: '2020', labelFr: 'Depuis', labelEn: 'Since' },
  { valueFr: '30+', valueEn: '30+', labelFr: 'Enseignants', labelEn: 'Teachers' },
  { valueFr: 'COGNIA', valueEn: 'COGNIA', labelFr: 'Accrédité', labelEn: 'Accredited' },
  { valueFr: '100%', valueEn: '100%', labelFr: 'Réussite Bac', labelEn: 'Bac Pass Rate' },
];

const teamMembers = [
  {
    name: 'Géraldine Guillermin',
    roleFr: 'Chef d\'Établissement',
    roleEn: 'Head of School',
    bioFr: 'Co-fondatrice d\'OFA Lycée. Plus de vingt ans d\'expérience comme enseignante, conseillère d\'orientation et directrice de lycée. Professeur d\'histoire-géographie. Créatrice d\'Education & Conseil, LLC. A mis en place la section américaine (OIB) du baccalauréat français et créé l\'unique centre d\'examen du Bac français en Nouvelle-Angleterre. Nommée « Chevalier dans l\'Ordre des Palmes Académiques » en 2016.',
    bioEn: 'Co-founder of OFA Lycée. Over twenty years of experience as a teacher, guidance counselor and high school director. History-Geography teacher. Creator of Education & Conseil, LLC. Established the American section (OIB) of the French baccalaureate and created the only French Bac examination center in New England. Named "Chevalier dans l\'Ordre des Palmes Académiques" in 2016.',
    linkedin: 'https://www.linkedin.com/in/g%C3%A9raldine-guillermin-46b90210',
  },
  {
    name: 'Olivier Saint-Vincent',
    roleFr: 'Directeur Académique',
    roleEn: 'Academic Director',
    bioFr: 'Co-fondateur d\'OFA Lycée. Auteur français d\'ouvrages scolaires, professeur certifié de Lettres Classiques et de Philosophie, avec plus de quinze ans d\'expérience d\'enseignement en milieu français et international aux États-Unis. Partisan de l\'enseignement numérique qu\'il définit comme « un rêve devenu réalité, dans lequel ni le temps ni l\'espace ne font obstacle à la transmission du savoir ».',
    bioEn: 'Co-founder of OFA Lycée. French author of educational books, certified teacher of Classical Literature and Philosophy, with over fifteen years of teaching experience in French and international settings in the United States. Advocate of digital education which he defines as "a dream come true, where neither time nor space stand in the way of knowledge transmission."',
    linkedin: 'https://www.linkedin.com/in/olivier-saint-vincent-81997b1a/',
  },
  {
    name: 'Valérie Burnick',
    roleFr: 'Directrice Administrative et Financière',
    roleEn: 'Administrative and Financial Director',
    bioFr: 'Co-fondatrice d\'OFA Lycée. Vingt ans d\'expérience dans l\'enseignement français et international. Professeur de Sciences Économiques et Sociales. A participé à l\'élaboration du programme d\'Économie pour le Baccalauréat International en 2013. A mis en place un programme d\'entrepreneuriat pour lycéens en collaboration avec Babson College. Diplômée en Sciences Économiques appliquées de l\'UCL en Belgique.',
    bioEn: 'Co-founder of OFA Lycée. Twenty years of experience in French and international education. Economics and Social Sciences teacher. Participated in developing the Economics curriculum for the International Baccalaureate in 2013. Established an entrepreneurship program for high school students in collaboration with Babson College. Graduate in Applied Economics from UCL in Belgium.',
    linkedin: 'https://www.linkedin.com/in/valerie-burnick-aa5090199/',
  },
];

export default function AboutPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'histoire');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const renderContent = () => {
    switch (activeTab) {
      case 'histoire':
        return (
          <div className="space-y-16">
            <div className="max-w-3xl">
              <p className="text-lg text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'OFA France est né du succès de notre école mère, OFA Lycée, fondée aux États-Unis en 2019. En l\'espace de cinq ans, OFA Lycée s\'est imposé comme l\'un des acteurs majeurs de l\'éducation bilingue franco-américaine. Fort de cette réussite — 100% de réussite au baccalauréat et 100% de mentions — OFA Lycée France a ouvert ses portes virtuelles en septembre 2024, un an après l\'ouverture au Royaume-Uni. C\'est aujourd\'hui plus de 800 élèves à travers le monde qui nous ont rejoints.'
                  : 'OFA France was born from the success of our parent school, OFA Lycée, founded in the United States in 2019. In five years, OFA Lycée has established itself as a major player in Franco-American bilingual education. Building on this success — 100% baccalaureate pass rate with 100% honors — OFA Lycée France opened its virtual doors in September 2024, one year after the opening in the United Kingdom. Today, over 800 students worldwide have joined us.'}
              </p>
            </div>
            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#E7EAEF] md:-translate-x-0.5" />
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className={`hidden md:block md:w-1/2 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                      <h3 className="text-xl font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                        {language === 'fr' ? item.titleFr : item.titleEn}
                      </h3>
                      <p className="text-[#475569] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {language === 'fr' ? item.descFr : item.descEn}
                      </p>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-[#2D78F6] rounded-full -translate-x-1.5 md:-translate-x-1.5 mt-2 z-10" />
                    <div className={`hidden md:block md:w-1/2 ${index % 2 === 0 ? 'pl-8' : 'pr-8'}`} />
                    {/* Mobile */}
                    <div className="md:hidden pl-10">
                      <h3 className="text-lg font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                        {language === 'fr' ? item.titleFr : item.titleEn}
                      </h3>
                      <p className="text-[#475569] text-sm mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {language === 'fr' ? item.descFr : item.descEn}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.05 * index }}
                  className="bg-[#E7EAEF] p-5 text-center"
                >
                  <p className="text-2xl font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    {language === 'fr' ? stat.valueFr : stat.valueEn}
                  </p>
                  <p className="text-xs text-[#475569] mt-1 uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? stat.labelFr : stat.labelEn}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'concept':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Avec notre concept unique de la complémentarité, OFA Lycée fusionne deux systèmes scolaires dans un parcours complet bilingue et biculturel de l\'élémentaire jusqu\'au lycée.'
                  : 'With our unique complementarity concept, OFA Lycée merges two school systems into a complete bilingual and bicultural pathway from elementary to high school.'}
              </p>
              <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'La journée, l\'élève est scolarisé dans un établissement local. Ensuite, rentré chez lui, il suit les cours complémentaires d\'OFA Lycée en ligne par petit groupe d\'élèves, centralisés sur notre plateforme d\'enseignement numérique.'
                  : 'During the day, the student attends their local school. Then, back home, they follow OFA Lycée\'s complementary online courses in small groups, centralized on our digital teaching platform.'}
              </p>
              <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Nos élèves ont la possibilité de suivre une double scolarité, leur ouvrant les portes aux études supérieures en Europe, au Royaume-Uni, en Amérique du Nord et dans le monde entier.'
                  : 'Our students have the opportunity to pursue dual schooling, opening doors to higher education in Europe, the UK, North America and worldwide.'}
              </p>
              <div className="bg-[#2D78F6]/5 p-6 border-l-4 border-[#2D78F6]">
                <h3 className="font-semibold text-[#14274B] mb-3" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {language === 'fr' ? 'Bénéfices' : 'Benefits'}
                </h3>
                <ul className="space-y-2">
                  {(language === 'fr' ? [
                    'Développement et maintien de la langue anglaise',
                    'Possibilité de passer le High School Diploma américain',
                    'Plus grand choix d\'études supérieures en France et à l\'étranger',
                    'Appartenance à une communauté anglophone',
                    'Social Emotional Learning intégré à tous les programmes',
                  ] : [
                    'Development and maintenance of English language skills',
                    'Opportunity to earn the American High School Diploma',
                    'Greater choice of higher education in France and abroad',
                    'Belonging to an English-speaking community',
                    'Social Emotional Learning integrated into all programs',
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#66CA9D] flex-shrink-0 mt-1" />
                      <span className="text-sm text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://images.pexels.com/photos/34113540/pexels-photo-34113540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt={language === 'fr' ? 'France et États-Unis' : 'France and United States'}
                className="w-full aspect-square object-cover"
              />
            </div>
          </div>
        );

      case 'vision':
        return (
          <div className="space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <div className="w-14 h-14 bg-[#2D78F6]/10 flex items-center justify-center mb-4">
                  <Eye className="w-7 h-7 text-[#2D78F6]" />
                </div>
                <h3 className="text-2xl font-light text-[#14274B] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {language === 'fr' ? 'Notre Vision' : 'Our Vision'}
                </h3>
                <p className="text-lg text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {language === 'fr'
                    ? 'Permettre à un grand nombre d\'élèves scolarisés en France de suivre une éducation bilingue d\'excellence.'
                    : 'Enable a large number of students enrolled in France to pursue an outstanding bilingual education.'}
                </p>
              </div>
              <div>
                <div className="w-14 h-14 bg-[#66CA9D]/10 flex items-center justify-center mb-4">
                  <Puzzle className="w-7 h-7 text-[#66CA9D]" />
                </div>
                <h3 className="text-2xl font-light text-[#14274B] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {language === 'fr' ? 'Notre Philosophie' : 'Our Philosophy'}
                </h3>
                <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {language === 'fr'
                    ? 'Le bilinguisme et le biculturalisme sont au coeur de notre concept. Cependant, notre mission va beaucoup plus loin.'
                    : 'Bilingualism and biculturalism are at the heart of our concept. However, our mission goes much further.'}
                </p>
              </div>
            </div>
            <div className="bg-[#14274B] p-8 md:p-12">
              <h3 className="text-xl font-light text-white mb-6" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Notre Approche Globale' : 'Our Holistic Approach'}
              </h3>
              <p className="text-white/70 leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Notre approche globale intègre une éducation complémentaire bilingue avec un apprentissage académique personnalisé, un programme d\'apprentissage de compétences socio-émotionnelles et une orientation individuelle vers le supérieur. Cette éducation complète offre à nos élèves les savoirs et savoir-faire ainsi que l\'expérience internationale dont ils ont besoin pour leur avenir.'
                  : 'Our holistic approach integrates a complementary bilingual education with personalized academic learning, a social-emotional skills program, and individual higher education guidance. This complete education provides our students with the knowledge, skills, and international experience they need for their future.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(language === 'fr' ? [
                  { title: 'Éducation Bilingue', desc: 'Apprentissage académique personnalisé en anglais' },
                  { title: 'Compétences Socio-émotionnelles', desc: 'Programme SEL intégré à chaque niveau' },
                  { title: 'Orientation Supérieure', desc: 'Accompagnement individuel vers les universités' },
                ] : [
                  { title: 'Bilingual Education', desc: 'Personalized academic learning in English' },
                  { title: 'Social-Emotional Skills', desc: 'SEL program integrated at every level' },
                  { title: 'Higher Ed Guidance', desc: 'Individual support for university applications' },
                ]).map((item, i) => (
                  <div key={i} className="bg-white/5 p-5">
                    <h4 className="font-semibold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{item.title}</h4>
                    <p className="text-white/60 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'equipe':
        return (
          <div className="space-y-12">
            <p className="text-lg text-[#475569] leading-relaxed max-w-3xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {language === 'fr'
                ? 'Unis par leur vision d\'un nouveau concept d\'éducation franco-américaine bilingue accessible à tous, les trois co-fondateurs d\'OFA Lycée sont enseignants, administrateurs et entrepreneurs qui ont chacun entre 15 et 20 ans d\'expérience dans l\'enseignement bilingue aux États-Unis.'
                : 'United by their vision of a new Franco-American bilingual education concept accessible to all, the three co-founders of OFA Lycée are teachers, administrators and entrepreneurs, each with 15 to 20 years of experience in bilingual education in the United States.'}
            </p>
            <div className="space-y-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  className="bg-[#E7EAEF] p-6 md:p-8"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="w-20 h-20 bg-[#14274B] flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-[#14274B]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                          {member.name}
                        </h3>
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#2D78F6] hover:text-[#14274B]">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      </div>
                      <p className="text-[#2D78F6] font-medium mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {language === 'fr' ? member.roleFr : member.roleEn}
                      </p>
                      <p className="text-[#475569] text-sm leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {language === 'fr' ? member.bioFr : member.bioEn}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'emplois':
        return (
          <div className="space-y-10">
            <div className="max-w-3xl">
              <p className="text-lg text-[#475569] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'OFA Lycée recherche des enseignants, coachs et des conseillers d\'orientation pour tous les niveaux de la Grande Section à la Terminale.'
                  : 'OFA Lycée is looking for teachers, coaches and guidance counselors for all levels from Kindergarten to 12th grade.'}
              </p>
              <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Vous êtes passionné(e) par l\'enseignement et êtes prêt(e) à rejoindre une équipe d\'enseignants dynamiques au sein d\'une institution scolaire franco-américaine innovante en pleine expansion ? L\'enseignement numérique vous intéresse et vous voulez vous investir dans le futur de l\'éducation Franco-Américaine en France ?'
                  : 'Are you passionate about teaching and ready to join a dynamic team of educators within an innovative and growing Franco-American school institution? Are you interested in digital education and want to invest in the future of Franco-American education in France?'}
              </p>
            </div>
            <div className="bg-[#E7EAEF] p-6 md:p-8">
              <h3 className="text-xl font-semibold text-[#14274B] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Compétences et expériences requises' : 'Required Skills and Experience'}
              </h3>
              <ul className="space-y-3">
                {(language === 'fr' ? [
                  'Sens prononcé de la bienveillance et de l\'exigence envers soi-même et ses élèves',
                  'Être titulaire d\'un diplôme d\'enseignement américain ou britannique',
                  'Adaptation et compréhension des élèves internationaux',
                  'Expérience de trois ans minimum dans l\'enseignement de la matière',
                  'Aptitudes au travail en équipe et à la collaboration',
                  'Bonnes aptitudes dans l\'encadrement d\'élèves dans un environnement en ligne',
                  'Bonnes capacités de communication avec les familles',
                  'Bonnes connaissances des systèmes d\'enseignement et des cultures des États-Unis et de France',
                ] : [
                  'Strong sense of caring and high standards for oneself and students',
                  'Hold an American or British teaching degree',
                  'Adaptability and understanding of international students',
                  'Minimum three years of teaching experience in the subject area',
                  'Teamwork and collaboration skills',
                  'Strong skills in managing students in an online environment',
                  'Good communication skills with families',
                  'Good knowledge of both US and French education systems and cultures',
                ]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#66CA9D] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Nous proposons des postes à temps partiels et horaires flexibles.'
                  : 'We offer part-time positions with flexible schedules.'}
              </p>
            </div>
            <a
              href="mailto:recruit@ofalycee.fr"
              className="inline-flex items-center gap-2 bg-[#2D78F6] text-white px-6 py-3 font-medium hover:bg-[#14274B] transition-colors"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <Mail className="w-4 h-4" />
              {language === 'fr' ? 'Envoyez-nous votre CV' : 'Send us your CV'}
            </a>
            <p className="text-sm text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>recruit@ofalycee.fr</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      <SEO path="/about" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'À Propos d\'OFA France' : 'About OFA France'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/70 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Construire l\'avenir académique international depuis la France.'
              : 'Building international academic futures from France.'}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Tab Navigation */}
      <section className="bg-white border-b border-[#E7EAEF] sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-1 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`about-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#2D78F6] text-[#2D78F6]'
                    : 'border-transparent text-[#475569] hover:text-[#14274B] hover:border-[#E7EAEF]'
                }`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <tab.icon className="w-4 h-4" />
                {language === 'fr' ? tab.labelFr : tab.labelEn}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Prêt à Construire l\'Avenir International de Votre Enfant ?' : 'Ready to Build Your Child\'s International Future?'}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="about-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr' ? 'S\'inscrire' : 'Register'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
